import io from "socket.io-client";

import { TechnicianContactsActions } from "./states/Contacts/TechnicianContactsState";
import { CustomerContactsActions } from "./states/Contacts/CustomerContactsState";

import { ContactTypes } from "./constants";
import MessagesHistory from "./sockets/messages-history";
import {
  UserCredentials,
  ContactsSubscribeCallback,
  IBaseContact,
  IContact,
  LogsItem,
  CrmUser,
  SelectMessagesOptions,
} from "./types";
import { COMMUNICATION_SERVER_URL, USER_CRM_TOKEN } from "./constants";
import LS from "../utils/LocalStorageManager";
import { Phone } from "../utils/Phone";
import { Department } from "../models";

let SOCKET_URL = COMMUNICATION_SERVER_URL;

const socket = io(SOCKET_URL, {
  path: "/socket.io",
});

class SocketTest {
  userCredentials: UserCredentials = {
    email: null,
    token: USER_CRM_TOKEN,
    phone: null,
    full_name: null,
    sid: "",
  };

  viewedWorkspace: UserCredentials | null = null;

  connection = false;
  pong = "";

  constructor() {
    socket.on("connect", () => {
      this.connection = true;
    });

    socket.on("disconnect", () => {
      this.connection = false;
    });

    // ??
    socket.on("pong", (response: any) => {
      this.pong = new Date().toISOString();
      console.log("C-SOCKET data: ", this.pong, " response: ", response);
    });
  }

  private saveCredentials(creds: UserCredentials) {
    this.userCredentials = {
      ...this.userCredentials,
      ...creds,
    };
  }

  takeCredentialsWithWorkspaceAccess() {
    if (!this.viewedWorkspace) {
      return this.userCredentials;
    }
    return this.viewedWorkspace;
  }

  setViewedWorkspace(workspaceData: UserCredentials | null = null) {
    this.viewedWorkspace = workspaceData;
  }

  sendToken() {
    socket.emit("token", USER_CRM_TOKEN);
  }

  authUser(
    username: string,
    email: string,
    sid: string,
    phone: string,
    userDepartments: number[],
    source?: any // CrmUser
  ) {
    this.saveCredentials({
      email: email,
      full_name: username,
      sid: sid,
      phone: phone,
      departments: userDepartments,
      source: source,
    } as UserCredentials);
    socket.emit("auth", this.userCredentials);
  }

  requestMessages(contact: IBaseContact | IContact) {
    const customSender = this.getCustomSender();

    MessagesHistory.requestMessagesHistoryForContact(socket, {
      contact: contact,
      credentials: this.takeCredentialsWithWorkspaceAccess(),
      customSender: customSender,
    });
  }

  requestMessagesWithPagination(
    contact: IBaseContact | IContact,
    options?: SelectMessagesOptions
  ) {
    const customSender = this.getCustomSender();

    MessagesHistory.requestMessagesHistoryForContact(socket, {
      contact: contact,
      credentials: this.takeCredentialsWithWorkspaceAccess(),
      customSender: customSender,
      options, // : { startDate : '' }
    });
  }

  requestContacts(callback: ContactsSubscribeCallback) {
    console.log(
      "[DBG] requestContacts: ",
      this.takeCredentialsWithWorkspaceAccess()
    );
    MessagesHistory.subscribeContactList(socket, callback);
    MessagesHistory.requestContacts(
      socket,
      this.takeCredentialsWithWorkspaceAccess()
    );
  }

  sendTypingData(user: Partial<UserCredentials>, contact: IBaseContact) {
    const customSender = this.getCustomSender();
    MessagesHistory.sendTypingData(socket, { user, contact, customSender });
  }

  checkWorkspaceAccess(callback: any) {
    MessagesHistory.checkWorkspaceAccess(
      socket,
      this.userCredentials,
      callback
    );
  }

  subscribeResultOutboundMessages(callback: (messages: any) => void) {
    MessagesHistory.subscribeResultOutboundMessages(socket, callback);
  }

  writeLogMessage(params: LogsItem) {
    MessagesHistory.writeLogsMessage(socket, params);
  }

  subscribeIncomingMessages(store: any) {
    MessagesHistory.subscribeIncomingMessages(
      socket,
      (incomingMessage: any) => {
        const state = store.getState();
        const selectedWorkspace =
          state["call-center"].cmnc_users.selectedWorkspace;
        const contactType = state["call-center"].messages.contactType;
        const contactsStateType =
          contactType === ContactTypes.TECHNICIAN
            ? "cmnc_tech_contacts"
            : "cmnc_cust_contacts";
        const currentActiveContact =
          state["call-center"][contactsStateType].activeContact;
        const worker = state["call-center"].crm.agent || {};
        const currentSender = LS.getSender();

        // if have selectedWorkspace - need to request messages when (incomingMessage.recipient === selectedWorkspace.phone)
        const workerPhone = selectedWorkspace
          ? selectedWorkspace.phone
          : worker.phone;

        if (
          new Phone(incomingMessage.recipient).normalized() ===
          new Phone(workerPhone).normalized()
        ) {
          // Update counters of incoming messages for contacts
          store.dispatch(
            TechnicianContactsActions.updateIncomingCounter(incomingMessage)
          );
          store.dispatch(
            CustomerContactsActions.updateIncomingCounter(incomingMessage)
          );

          // If the incomingMessage.sender is our active contact - request messages history for him
          if (currentActiveContact.phone === incomingMessage.sender) {
            this.requestMessages({ phone: incomingMessage.sender });
          }
        } else if (
          currentSender &&
          currentSender.phone === incomingMessage.recipient
        ) {
          // departmentPhone
          // Update counters of incoming messages for contacts
          store.dispatch(
            TechnicianContactsActions.updateIncomingCounter(incomingMessage)
          );
          store.dispatch(
            CustomerContactsActions.updateIncomingCounter(incomingMessage)
          );

          // -- request messages for worker.phone and currentSenderPhone (this is setup in credentials)
          // If the sender is our active contact - request messages history for him
          if (currentActiveContact.phone === incomingMessage.sender) {
            this.requestMessages({ phone: incomingMessage.sender });
          }
        }
      }
    );
  }

  subscribeToOutboundMessages(store: any) {
    MessagesHistory.subscribeToOutboundMessages(socket, (info) => {
      const state = store.getState();
      const selectedWorkspace =
        state["call-center"].cmnc_users.selectedWorkspace;
      const infoSenderPhone = new Phone(info.sender || "").normalized();
      const infoRecipientPhone = new Phone(info.recipient || "").normalized();
      const wsPhone = new Phone(selectedWorkspace?.phone || "").normalized();

      if (wsPhone.length > 2 && wsPhone === infoSenderPhone) {
        this.requestMessages({ phone: infoRecipientPhone });
      }
    });
  }

  subscribeMessageStatusUpdating(store: any) {
    MessagesHistory.subscribeMessageStatus(socket, (updatedMessage: any) => {
      const state = store.getState();
      const contactType = state["call-center"].messages.contactType;

      if (contactType === ContactTypes.TECHNICIAN) {
        store.dispatch(
          TechnicianContactsActions.technicianUpdateMessageStatus(
            updatedMessage
          )
        );
      } else if (contactType === ContactTypes.CUSTOMER) {
        store.dispatch(
          CustomerContactsActions.customerUpdateMessageStatus(updatedMessage)
        );
      }
    });
  }

  subscribeTypingData(callback: (messages: any) => void) {
    MessagesHistory.subscribeTypingData(socket, callback);
  }

  sendTestMesssage() {
    socket.emit("send-test-message");
  }

  sendUserMessage(messageData: any) {
    const customSender = this.getCustomSender();

    socket.emit("send-message", {
      messageMeta: messageData,
      contact: messageData.recipient,
      credentials: this.userCredentials,
      customSender: customSender,
    });
  }

  updateContact(contact: IContact) {
    socket.emit("update-contact", contact);
    this.requestMessages(contact);
  }

  disconnect() {
    socket.off("connect");
    socket.off("disconnect");
    socket.off("pong");
  }

  private getCustomSender(): Department {
    const customSender = LS.getSender();

    if (customSender) {
      customSender.phone = new Phone(customSender.phone).normalized();
    }
    return customSender;
  }
}

const socketTest = new SocketTest();

export default socketTest;
